const FAILURE_CODE = 'workflow_integration_readiness_blocked';

const SECTION_ITEMS = Object.freeze({
  'company-context': ['active-company', 'company-profile', 'role'],
  'brand-profile': ['brand-created', 'brand-name', 'brand-website', 'brand-slug'],
  integrations: ['integration-meta'],
  'video-voice': ['voice-profile', 'asset-source'],
  'first-campaign': ['campaign-brand', 'campaign-prerequisites'],
});

const SECTION_ORDER = Object.keys(SECTION_ITEMS);

const ALLOWED_PROVIDER_STATUSES = new Set([
  'missing',
  'untested',
  'needs_reauth',
  'disabled',
  'unknown',
]);

function text(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function checklistBlocker(blocker) {
  const sectionId = text(blocker?.sectionId);
  const itemId = text(blocker?.itemId);
  if (!SECTION_ITEMS[sectionId]?.includes(itemId)) return null;
  const provider = text(blocker?.provider);
  const status = text(blocker?.status);
  return {
    sectionId,
    itemId,
    ...(sectionId === 'integrations' && provider === 'meta' ? {
      provider: 'meta',
      status: ALLOWED_PROVIDER_STATUSES.has(status) ? status : 'unknown',
    } : {}),
  };
}

function integrationBlocker(blocker) {
  const provider = text(blocker?.provider);
  const status = text(blocker?.status);
  if (provider !== 'meta') return null;
  return {
    sectionId: 'integrations',
    itemId: 'integration-meta',
    provider: 'meta',
    status: ALLOWED_PROVIDER_STATUSES.has(status) ? status : 'unknown',
  };
}

export function sanitizeCampaignLaunchReadiness(value) {
  const source = value?.checklist || value?.readiness || value || {};
  const input = Array.isArray(source?.blockers) ? source.blockers : [];
  const blockers = [];
  const seen = new Set();

  for (const entry of input) {
    const blocker = checklistBlocker(entry) || integrationBlocker(entry);
    if (!blocker) continue;
    const key = `${blocker.sectionId}:${blocker.itemId}:${blocker.status || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    blockers.push(blocker);
  }

  const role = text(source?.company?.role || source?.role);
  blockers.sort((left, right) => {
    const sectionDifference = SECTION_ORDER.indexOf(left.sectionId) - SECTION_ORDER.indexOf(right.sectionId);
    if (sectionDifference) return sectionDifference;
    return SECTION_ITEMS[left.sectionId].indexOf(left.itemId)
      - SECTION_ITEMS[right.sectionId].indexOf(right.itemId);
  });

  return {
    blockers: blockers.slice(0, 6),
    role: ['owner', 'admin', 'marketer', 'viewer'].includes(role) ? role : null,
  };
}

export function createCampaignLaunchReadinessError(checklist) {
  const error = new Error(FAILURE_CODE);
  error.code = FAILURE_CODE;
  error.readiness = sanitizeCampaignLaunchReadiness(checklist);
  return error;
}

function effectiveSections(blockers) {
  const sections = [...new Set(blockers.map((blocker) => blocker.sectionId))];
  if (sections.length > 1) return sections.filter((sectionId) => sectionId !== 'first-campaign');
  return sections;
}

function multiSectionMessage(sections) {
  const labels = {
    'company-context': 'company access',
    'brand-profile': 'the brand profile',
    integrations: 'the Meta Ads connection',
    'video-voice': 'platform media services',
    'first-campaign': 'launch readiness',
  };
  const names = sections.map((sectionId) => labels[sectionId]).filter(Boolean);
  if (names.length <= 2) {
    return `Resolve ${names.join(' and ')} before launching this campaign.`;
  }
  return `Resolve ${names.slice(0, 2).join(', ')} and ${names.length - 2} other launch requirement${names.length === 3 ? '' : 's'} before continuing.`;
}

export function campaignLaunchReadinessPresentation(error) {
  const code = text(error?.code || error?.error || error?.safeCode);
  if (code !== FAILURE_CODE) return null;

  const readiness = sanitizeCampaignLaunchReadiness(error?.readiness);
  const blockers = readiness.blockers;
  const sections = effectiveSections(blockers);
  const roleBlocked = blockers.some((blocker) => blocker.itemId === 'role');

  if (roleBlocked || readiness.role === 'viewer') {
    return {
      message: 'Your current role cannot create or launch campaigns for this company.',
      actionLabel: 'Return home',
      actionRoute: 'home',
      blockers,
    };
  }

  if (sections.length > 1) {
    return {
      message: multiSectionMessage(sections),
      actionLabel: 'View launch readiness',
      actionRoute: 'onboarding',
      blockers,
    };
  }

  switch (sections[0]) {
    case 'company-context':
      return {
        message: 'Complete the active company setup before launching this campaign.',
        actionLabel: 'Review company',
        actionRoute: 'company',
        blockers,
      };
    case 'brand-profile':
      return {
        message: 'Complete the brand profile before launching this campaign.',
        actionLabel: 'Review company',
        actionRoute: 'company',
        blockers,
      };
    case 'integrations':
      {
        const metaStatus = blockers.find((blocker) => blocker.provider === 'meta')?.status;
        if (metaStatus === 'missing') {
          return {
            message: 'Connect Meta Ads before this campaign can launch.',
            actionLabel: 'Connect Meta Ads',
            actionRoute: 'settings',
            blockers,
          };
        }
        if (metaStatus === 'needs_reauth') {
          return {
            message: 'Reconnect Meta Ads before this campaign can launch.',
            actionLabel: 'Reconnect Meta Ads',
            actionRoute: 'settings',
            blockers,
          };
        }
        if (metaStatus === 'untested') {
          return {
            message: 'Check the Meta Ads connection before this campaign can launch.',
            actionLabel: 'Review Meta Ads',
            actionRoute: 'settings',
            blockers,
          };
        }
        if (metaStatus === 'disabled') {
          return {
            message: 'Meta Ads is disconnected for this company.',
            actionLabel: 'Review Meta Ads',
            actionRoute: 'settings',
            blockers,
          };
        }
      }
      return {
        message: 'Meta Ads connection needs attention before this campaign can launch.',
        actionLabel: 'Review Meta Ads',
        actionRoute: 'settings',
        blockers,
      };
    case 'video-voice':
      return {
        message: 'Beyond Leads media services need operator attention before this campaign can launch.',
        actionLabel: null,
        actionRoute: null,
        blockers,
      };
    default:
      return {
        message: 'Campaign launch requirements need attention before this campaign can launch.',
        actionLabel: 'View launch readiness',
        actionRoute: 'onboarding',
        blockers,
      };
  }
}
